import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { auditLogsAPI } from '../services/api';
import './AuditLogs.css';
import { useAuthStore } from '../store/authStore';
import { formatDateTimeForTimezone } from '../utils/datetime';
import DataTable from '../components/DataTable';

export default function AuditLogs() {
  const { timezone } = useAuthStore();
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    user_id: '',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async (overrideFilters) => {
    setLoading(true);
    try {
      const effectiveFilters = overrideFilters || filters;
      const params = {};
      if (effectiveFilters.action) params.action = effectiveFilters.action;
      if (effectiveFilters.entity_type) params.entity_type = effectiveFilters.entity_type;
      if (effectiveFilters.user_id) params.user_id = effectiveFilters.user_id;
      if (effectiveFilters.start_date) params.start_date = effectiveFilters.start_date;
      if (effectiveFilters.end_date) params.end_date = effectiveFilters.end_date;
      
      const response = await auditLogsAPI.getAll(params);
      setAuditLogs(response.data || []);
    } catch (error) {
      toast.error('Error fetching audit logs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const parseChanges = (changesStr) => {
    if (!changesStr) return null;
    try {
      return typeof changesStr === 'string' ? JSON.parse(changesStr) : changesStr;
    } catch (e) {
      console.error('Error parsing changes:', e);
      return changesStr;
    }
  };

  const formatChanges = (changes) => {
    if (!changes) return '-';
    const parsed = parseChanges(changes);
    if (!parsed) return '-';
    
    if (typeof parsed === 'object') {
      return Object.entries(parsed)
        .map(([key, value]) => `${key}: ${typeof value === 'object' ? JSON.stringify(value) : value}`)
        .join('\n');
    }
    return parsed;
  };

  const getUniqueValues = (field) => {
    const values = new Set();
    auditLogs.forEach(log => {
      if (log[field]) values.add(log[field]);
    });
    return Array.from(values).sort();
  };

  const filteredLogs = auditLogs.filter(log => {
    if (filters.action && log.action !== filters.action) return false;
    if (filters.entity_type && log.entity_type !== filters.entity_type) return false;
    return true;
  });

  const handleFilterChange = (field, value) => {
    setFilters({...filters, [field]: value});
  };

  const columns = [
    { key: 'id', label: 'ID', width: '80px', sortable: true },
    { key: 'user_id', label: 'User', className: 'user-cell', sortable: true },
    {
      key: 'action',
      label: 'Action',
      render: (value) => (
        <span className={`action-badge action-${(value || '').toLowerCase()}`}>
          {value}
        </span>
      ),
      sortable: true,
    },
    { key: 'entity_type', label: 'Entity', className: 'entity-type', sortable: true },
    { key: 'entity_id', label: 'Entity ID', sortable: true },
    {
      key: 'description',
      label: 'Description',
      className: 'description-cell',
      sortable: false,
    },
    {
      key: 'ip_address',
      label: 'IP Address',
      className: 'ip-cell',
      sortable: false,
    },
    {
      key: 'created_at',
      label: 'Timestamp',
      render: (value) => formatDateTimeForTimezone(value, timezone),
      className: 'timestamp-cell',
      sortable: true,
    },
  ];

  const renderChangesDetails = (row) => (
    <div className="details-content">
      <h4>Changes</h4>
      <pre className="changes-display">
        {formatChanges(row.changes)}
      </pre>
    </div>
  );

  return (
    <div className="container audit-logs-container">
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '8px' }}>
        <h1 className="page-title">Audit Logs</h1>
      </div>

      <div className="filter-section">
        <h3>Filter Audit Logs</h3>
        <div className="filters-section">
          <div className="filter-group">
            <label>Action:</label>
            <select 
              value={filters.action} 
              onChange={(e) => handleFilterChange('action', e.target.value)}
            >
              <option value="">All Actions</option>
              <option value="CREATE">CREATE</option>
              <option value="READ">READ</option>
              <option value="UPDATE">UPDATE</option>
              <option value="DELETE">DELETE</option>
              {getUniqueValues('action')
                .filter(action => !['CREATE', 'READ', 'UPDATE', 'DELETE'].includes(action))
                .map(action => (
                  <option key={action} value={action}>{action}</option>
                ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Entity Type:</label>
            <select 
              value={filters.entity_type} 
              onChange={(e) => handleFilterChange('entity_type', e.target.value)}
            >
              <option value="">All Entity Types</option>
              {getUniqueValues('entity_type').map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>User ID:</label>
            <select 
              value={filters.user_id} 
              onChange={(e) => handleFilterChange('user_id', e.target.value)}
            >
              <option value="">All Users</option>
              {getUniqueValues('user_id').map(userId => (
                <option key={userId} value={userId}>{userId}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Start Date:</label>
            <input 
              type="date"
              value={filters.start_date} 
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>End Date:</label>
            <input 
              type="date"
              value={filters.end_date} 
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button
            className="btn btn-primary"
            onClick={() => fetchAuditLogs()}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Apply Filters'}
          </button>
          <button 
            onClick={() => {
              const emptyFilters = { action: '', entity_type: '', user_id: '', start_date: '', end_date: '' };
              setFilters(emptyFilters);
              fetchAuditLogs(emptyFilters);
            }}
            className="btn btn-secondary"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading audit logs...</div>
      ) : filteredLogs.length === 0 ? (
        <div className="empty-state">
          <p>No audit logs found</p>
        </div>
      ) : (
        <DataTable
          data={filteredLogs}
          columns={columns}
          itemsPerPage={20}
          searchableFields={['description', 'entity_type', 'ip_address']}
          title="Audit Logs List"
          expandableRowRenderer={renderChangesDetails}
        />
      )}

      <div className="logs-footer">
        <p>Showing {filteredLogs.length} total {filteredLogs.length === 1 ? 'log' : 'logs'}</p>
      </div>
    </div>
  );
}
